import classnames from 'classnames';
import { API } from 'common';
import BottomSheet, { useBottomSheetRef } from 'components/BottomSheet';
import MyDatePicker from 'components/DatePicker';
import MyDateRangePicker from 'components/DatePicker/Range';
import Loader from 'components/Loader';
import Page from 'components/Page';
import Empty from 'components/new-appointment/Empty';
import moment from 'moment';
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import { formatCurrency } from 'utils/formatCurrency';
import QueryString from 'utils/queryString';
import useDebounce from 'utils/useDebounce';

const FORMAT_DATE = 'YYYY-MM-DD';

const sortOptions = [
  { label: 'Ascending', value: 'asc' },
  { label: 'Descending', value: 'desc' },
];

const EmployeeSalariesV2 = () => {
  const bsRef = useBottomSheetRef();
  const [loading, setLoading] = React.useState(false);
  const [activeTab, setActiveTab] = useState('DAY');
  const [sort, setSort] = useState(sortOptions?.[0].value);
  const staffListRef = useRef();
  const [dataList, setDataList] = useState([]);
  const currentParamRef = useRef('');

  const staffGroupRef = useRef(null);

  const toggle = (tab) => {
    let range_param = '';
    const now = new Date();
    switch (tab) {
      case 'WEEK': {
        const dateMoment = moment(now);
        const start_date = dateMoment.startOf('week').format(FORMAT_DATE);
        const end_date = dateMoment.endOf('week').format(FORMAT_DATE);
        range_param = `startDate=${start_date}&endDate=${end_date}`;
        break;
      }
      case 'MONTH': {
        const dateMoment = moment(now);
        const start_date = dateMoment.startOf('month').format(FORMAT_DATE);
        const end_date = dateMoment.endOf('month').format(FORMAT_DATE);
        range_param = `startDate=${start_date}&endDate=${end_date}`;
        break;
      }
      case 'DAY':
      default: {
        const dateMoment = moment(now).format(FORMAT_DATE);
        range_param = `startDate=${dateMoment}&endDate=${dateMoment}`;
        break;
      }
    }
    fetchData(range_param, sort);
    if (activeTab !== tab) setActiveTab(tab);
  };

  const fetchData = async (range_param, sortValue) => {
    setLoading(true);
    const date = QueryString.parse(range_param);
    const payload = {
      endDate: date.endDate,
      startDate: date.startDate,
      sort: sortValue || sort,
    };
    currentParamRef.current = range_param;
    try {
      let response = await API.post(`staff/staffSalaryV2`, { payload });
      if (response.status === 200) {
        let { payload } = response.data;
        if (payload) {
          setDataList(payload);
        }
      }
    } catch (error) {
      console.error('get_summary_report_data', error);
    } finally {
      setLoading(false);
    }
  };

  const employeeSummaryData = useMemo(() => {
    return ({
      totalCash: dataList?.totalCash || 0,
      totalCheck: dataList?.totalCheck || 0,
    })
  }, [dataList]);
  const employees = useMemo(() => {
    return dataList?.employees?.map((o, i) => ({
      id: o?.staffId || i,
      staffName: o?.staffName || '',
      CA: o?.ca || 0,
      CH: o?.ch || 0,
      sale: {
        subValue: (o?.salesServices || 0) > 1 ? (o.salesServices + ' services') : (o?.salesServices || 0) + ' service',
        value: o?.sales || 0,
      },
      supply: {
        subValue: '--',
        value: o?.supply || 0,
      },
      comm: {
        subValue: (o?.commissionPercent || 0) + '%',
        value: o?.commissionValue || 0,
      },
      tip: {
        subValue: formatCurrency(o?.tipFee || 0),
        value: o?.tip || 0,
        tipPercent: o?.tipPercent || 0,
      },
      disc: {
        subValue: o?.discountSourceType === "OWNER" ? 'Owner disc' : 'Staff disc',
        value: o?.discountValue || 0,
      },
      total: o?.total || 0,
      payment: {
        cash: o?.cash || 0,
        cashPercent: o?.cashPercent || 0,
        check: o?.check || 0,
        checkPercent: o?.checkPercent || 0,
        tipToCheck: o?.tipToCheck || 0,
        tipToCash: o?.tipToCash || 0,
      }
    }))
  }, [dataList]);


  useEffect(() => toggle('WEEK'), []);

  const handleSearch = (text) => {
    if (!staffListRef.current) return;
    staffListRef.current.search(text);
  };

  const Header = () => (
    <Nav tabs className="overview-custom-nav custom-nav-light">
      <NavItem className="tab-item">
        <NavLink
          className={classnames({ active: activeTab === 'DAY' })}
          onClick={() => {
            toggle('DAY');
          }}
        >
          TODAY
        </NavLink>
      </NavItem>
      <NavItem className="tab-item">
        <NavLink
          className={classnames({ active: activeTab === 'WEEK' })}
          onClick={() => {
            toggle('WEEK');
          }}
        >
          WEEKLY
        </NavLink>
      </NavItem>
      <NavItem className="tab-item">
        <NavLink
          className={classnames({ active: activeTab === 'MONTH' })}
          onClick={() => {
            toggle('MONTH');
          }}
        >
          MONTHLY
        </NavLink>
      </NavItem>
      <NavItem className="tab-item">
        <NavLink
          className={classnames({ active: activeTab === 'RANGE' })}
          onClick={() => {
            toggle('RANGE');
          }}
        >
          RANGE
        </NavLink>
      </NavItem>
    </Nav>
  )

  const onChooseOption = (o) => {
    setSort(o.value);
    fetchData(currentParamRef.current, o.value);
    bsRef.current.close();
  };

  return (
    <>
      {loading && <Loader />}
      <Page menu title="Employee Salaries" Sticky={Header}  >
        <Container className="overview m-0 p-0 employee_salaries_kit employee_salaries_kit_v2" style={{ backgroundColor: '#F3F3F3' }}>
          <div className='employee_salaries_kit_staffs_group' ref={staffGroupRef}>
            <TabContent activeTab={activeTab} style={{ color: "#000000" }}>
              <TabPane tabId="DAY">
                <div className="date_picker_container">
                  <MyDatePicker daily unsetMaxDate stringData={fetchData} format={FORMAT_DATE}
                    customRender={({ icon, date }) => {
                      return (
                        <div className='date_picker_label_custom' >
                          <span>Date: {`${date?.format("ddd")}, `}{date?.format('MM-DD-YYYY')}</span>
                          {icon}
                        </div>)
                    }}
                  />
                </div>
              </TabPane>
              <TabPane tabId="WEEK">
                <div className="date_picker_container">
                  <MyDatePicker weekly unsetMaxDate stringData={fetchData} format={FORMAT_DATE}
                    customRender={({ icon, start, end }) => {
                      return (
                        <div className='date_picker_label_custom' >
                          <span> From: <span className='highlight'>{start?.format('MM-DD-YYYY')}</span> - To: <span className='highlight'>{end.format('MM-DD-YYYY')}</span>
                          </span>
                          {icon}
                        </div>)
                    }}
                  />
                </div>
              </TabPane>
              <TabPane tabId="MONTH">
                <div className="date_picker_container month">
                  <MyDatePicker monthly unsetMaxDate stringData={fetchData} format={FORMAT_DATE}
                    customRender={({ icon, date }) => {
                      return (
                        <div className='date_picker_label_custom'>
                          <span> Salary month {date?.format('MMMM')}</span>
                          {icon}
                        </div>)
                    }}
                  />
                </div>
              </TabPane>
              <TabPane tabId="RANGE">
                <div className="date_picker_container range">
                  <div className='top-label'>Salary range</div>
                  <MyDateRangePicker showLabel={false} stringData={fetchData} format={FORMAT_DATE} />
                </div>
              </TabPane>
            </TabContent>
            <div className="filter_group">
              <InputSearch onDebounce={handleSearch} />
              <div className='btn-sort' onClick={() => bsRef.current.open()}><SortIcon /></div>
            </div>
            <StaffSalariesTable ref={staffListRef} data={employees} employeeSummaryData={employeeSummaryData} />
          </div>
        </Container>
      </Page>
      <BottomSheet
        ref={bsRef}
        autoHeight
        className='filter_option_kit_container_bs'
        title={'Sort'}
      >
        <div className='filter_option_kit_container_wrap' style={{ paddingBottom: '60px' }}>
          <div className='filter_option_kit_items'>
            {sortOptions.map(o => (
              <button key={o.value} className={`filter_option_kit_item ${sort === o.value ? 'active' : ''}`} onClick={() => onChooseOption(o)}>
                <span>{o.label}</span>
                {sort === o.value && <IconChecked />}
              </button>
            ))}
          </div>
        </div>
      </BottomSheet>
    </>
  )
}

export default EmployeeSalariesV2;

export const StaffSalariesTable = React.forwardRef(({ data = [], employeeSummaryData }, ref) => {
  const [search, setSearch] = useState("");

  const staffs = useMemo(() => {
    const str = search?.trim()?.toLowerCase();
    if (!str) return data;
    return data.filter((o) =>
      String(o.staffName || "")
        ?.toLowerCase()
        ?.includes(str)
    );
  }, [data, search]);

  const totals = useMemo(() => {
    let disc = 0, check = 0, supply = 0, tip = 0, sale = 0, cash = 0;
    staffs.map(o => {
      disc += o.disc.value;
      supply += o.supply.value;
      tip += o.tip.value;
      sale += o.sale.value;
      cash += o.payment.cash;
      check += o.payment.check;
    });

    return ({
      disc, supply, tip, sale, cash, check
    })
  }, [staffs]);

  useImperativeHandle(ref, () => ({
    search: (text) => setSearch(text),
  }));

  if (!staffs?.length) return <Empty message={'No data'} />;

  return (
    <div className="employee_salaries_kit_staffs">
      {/* <div className="employee_salaries_kit_summary">
        <div className="summary-item">
          <span className='summary-label'>Total Cash</span>
          <div className="divider-dotted" />
          <span className='summary-value'>{formatCurrency(employeeSummaryData?.totalCash)}</span>
        </div>
        <div className="summary-item">
          <span className='summary-label'>Total Check</span>
          <div className="divider-dotted" />
          <span className='summary-value'>{formatCurrency(employeeSummaryData?.totalCheck)}</span>
        </div>
        <div className="divider-line" />
        <div className="summary-item master">
          <span className='summary-label'>EMPLOYEE SALARIES</span>
          <div className="divider-dotted" />
          <span className='summary-value'>{formatCurrency(employeeSummaryData?.totalCash + employeeSummaryData?.totalCheck)}</span>
        </div>
      </div> */}
      {/* <div className="employee_salaries_kit_title_section">EMPLOYEE REPORT</div> */}
      <table className='employee_salaries_kit_staffs_table'>
        <thead>
          <tr>
            <th className='name'>NAME</th>
            <th>DISC</th>
            <th>SUPPLY</th>
            <th>TIP</th>
            <th>SALES</th>
            <th>CASH/<br />BONUS</th>
            <th>CHECK</th>
          </tr>
          <tr>
            <td colSpan={7}>
              <div className='divider-top'></div>
            </td>
          </tr>
        </thead>
        <tbody>
          {staffs.map(o => (
            <tr key={o.id} >
              <td className='name'><span>{o.staffName}</span></td>
              <td>{formatCurrency(o.disc.value)}</td>
              <td>{formatCurrency(o.supply.value)}</td>
              <td>{formatCurrency(o.tip.value)}</td>
              <td>{formatCurrency(o.sale.value)}</td>
              <td>{formatCurrency(o.payment.cash)}</td>
              <td>{formatCurrency(o.payment.check)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={7}>
              <div className='divider-bottom'></div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td className='name'>Total</td>
            <td>{formatCurrency(totals.disc)}</td>
            <td>{formatCurrency(totals.supply)}</td>
            <td>{formatCurrency(totals.tip)}</td>
            <td>{formatCurrency(totals.sale)}</td>
            <td>{formatCurrency(totals.cash)}</td>
            <td>{formatCurrency(totals.check)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
});

const InputSearch = ({ onDebounce, onFocus }) => {
  const [text, onChange] = useDebounce("", onDebounce);
  return (
    <div className="search-box">
      <Search />
      <input
        value={text}
        onChange={onChange}
        type="search"
        placeholder="Staff Name"
        onFocus={onFocus}
      />
    </div>
  );
};

const Search = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > <g clipPath="url(#clip0_7_25711)"> <path fillRule="evenodd" clipRule="evenodd" d="M12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75ZM2.25 12C2.25 6.61522 6.61522 2.25 12 2.25C17.3848 2.25 21.75 6.61522 21.75 12C21.75 17.3848 17.3848 21.75 12 21.75C6.61522 21.75 2.25 17.3848 2.25 12Z" fill="#686868" /> <path fillRule="evenodd" clipRule="evenodd" d="M17.487 17.9555C17.7796 17.6622 18.2544 17.6616 18.5477 17.9541L22.0717 21.469C22.365 21.7615 22.3656 22.2364 22.0731 22.5297C21.7806 22.8229 21.3057 22.8235 21.0125 22.531L17.4884 19.0162C17.1952 18.7236 17.1945 18.2488 17.487 17.9555Z" fill="#686868" /> </g> <defs> <clipPath id="clip0_7_25711"> <rect width="24" height="24" fill="white" /> </clipPath> </defs> </svg>);
const SortIcon = () => (<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path fillRule="evenodd" clipRule="evenodd" d="M8.64415 6.37713C10.1686 5.12275 12.3818 5.17389 13.8467 6.49734C15.3117 7.8208 15.5865 10.0174 14.4929 11.661L14.4916 11.6628C14.0538 12.3182 13.4321 12.8296 12.7048 13.1331L12.7027 13.134C10.8793 13.8906 8.7767 13.1978 7.76018 11.5054C6.74362 9.81307 7.11969 7.63149 8.64415 6.37713ZM12.2962 8.21358C11.6817 7.65839 10.7533 7.63693 10.1137 8.16315L10.1137 8.16315C9.4742 8.68936 9.31645 9.60454 9.74289 10.3145L9.7429 10.3145C10.1692 11.0242 11.0506 11.3148 11.8153 10.9981C12.1216 10.87 12.3834 10.6546 12.5679 10.3788C13.026 9.68939 12.9105 8.76849 12.2962 8.21358Z" fill="#0C719C" /> <path fillRule="evenodd" clipRule="evenodd" d="M20.9861 18.507C20.3466 17.9807 19.4182 18.0022 18.8037 18.5574C18.1894 19.1124 18.074 20.0333 18.532 20.7226C18.7166 20.9985 18.9784 21.2139 19.2846 21.3419C20.0492 21.6586 20.9307 21.368 21.3569 20.6583L21.357 20.6582C21.7835 19.9483 21.6258 19.0333 20.9861 18.507L20.9861 18.507ZM22.4556 16.721C20.9312 15.4666 18.7181 15.5177 17.2531 16.8412C15.7882 18.1646 15.5134 20.3613 16.6071 22.0048L16.6083 22.0066C17.0461 22.6619 17.6678 23.1735 18.3952 23.4769L18.3971 23.4777C20.2206 24.2344 22.3231 23.5416 23.3396 21.8493C24.3564 20.1569 23.9803 17.9754 22.4557 16.721M18.8404 22.4096L18.8404 22.4096L18.8404 22.4096Z" fill="#0C719C" /> <path d="M14.008 8.28497C13.3694 8.28497 12.8516 8.80272 12.8516 9.44142C12.8516 10.0801 13.3694 10.5979 14.008 10.5979V8.28497ZM26.3435 10.5979C26.9822 10.5979 27.5 10.0801 27.5 9.44142C27.5 8.80272 26.9822 8.28497 26.3435 8.28497V10.5979ZM8.34451 10.5979C8.9832 10.5979 9.50096 10.0801 9.50096 9.44142C9.50096 8.80272 8.9832 8.28497 8.34451 8.28497V10.5979ZM4.75643 8.28497C4.11774 8.28497 3.59998 8.80272 3.59998 9.44142C3.59998 10.0801 4.11774 10.5979 4.75643 10.5979V8.28497ZM17.0919 20.6319C17.7306 20.6319 18.2484 20.1141 18.2484 19.4754C18.2484 18.8367 17.7306 18.319 17.0919 18.319V20.6319ZM4.75643 18.319C4.11774 18.319 3.59998 18.8367 3.59998 19.4754C3.59998 20.1141 4.11774 20.6319 4.75643 20.6319V18.319ZM22.7554 18.319C22.1168 18.319 21.599 18.8367 21.599 19.4754C21.599 20.1141 22.1168 20.6319 22.7554 20.6319V18.319ZM26.3435 20.6319C26.9822 20.6319 27.5 20.1141 27.5 19.4754C27.5 18.8367 26.9822 18.319 26.3435 18.319V20.6319ZM14.008 10.5979H26.3435V8.28497H14.008V10.5979ZM8.34451 8.28497H4.75643V10.5979H8.34451V8.28497ZM17.0919 18.319H4.75643V20.6319H17.0919V18.319ZM22.7554 20.6319H26.3435V18.319H22.7554V20.6319Z" fill="#0C719C" /> </svg>)
const IconChecked = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"> <path d="M9.72692 18C9.40676 18 9.10261 17.8661 8.8785 17.6317L4.34817 12.8936C3.88394 12.4081 3.88394 11.6045 4.34817 11.1189C4.8124 10.6334 5.58078 10.6334 6.04501 11.1189L9.72692 14.9697L17.955 6.36414C18.4192 5.87862 19.1876 5.87862 19.6518 6.36414C20.1161 6.84967 20.1161 7.6533 19.6518 8.13882L10.5753 17.6317C10.3512 17.8661 10.0471 18 9.72692 18Z" fill="#0C719C" /> </svg>);
